/* eslint-disable max-len */
import React, {
  ComponentType,
  useState
} from 'react'
import {
  Icon,
  Modal,
  Popup,
  Table,
  Loader,
  Dimmer
} from 'semantic-ui-react'
// Local
import { Encounter } from '../../authenticated/types'
import Flyout from '../../common/Flyout'
import DocumentIcon from '../../icons/DocumentIcon'
import TablePaginationFooter from '../common/TablePaginationFooter'
import { api } from '../../../action/api'
import { API_URL, shouldRenderReskin2025 } from '../../../constant'
import Close from '../../icons/close'
import If from '../../common/if'
import ListIcon from '../../icons/ListIcon'
import { reveleerStatusLabelType } from '../../ReveleerStatusLabel'

const SafeTable = Table as unknown as ComponentType<any>

type Chart = {
  documentId: string
  documentIdType: string
  chartDate: string
  documentType: string
  organizationName: string
  documentName: string
  sourceType: 'pdf' | 'xml'
  lastReviewed: string | null
}

type ChartsPayload = {
  pagination: {
    limit: number
    offset: number
    total: number
  }
  sort: {
    column: string
    direction: 'asc' | 'desc'
  }
  charts: Chart[]
}

type Props = {
  encounter: Encounter
}

const SORT_COLUMNS = {
  chartDate: {
    label: 'Chart Date',
    direction: 'desc'
  },
  documentType: {
    label: 'Document Type',
    direction: 'asc'
  },
  organization: {
    label: 'Organization Name',
    direction: 'asc'
  }
} as const

type SortColumn = keyof typeof SORT_COLUMNS


function CDIChart(props: Props): JSX.Element {
  const { encounter } = props

  const [
    isFlyoutOpen,
    setIsFlyoutOpen
  ] = useState<boolean>(false)

  const [
    chartsData,
    setChartsData
  ] = useState<ChartsPayload | null>(null)

  const [
    isLoadingCharts,
    setIsLoadingCharts
  ] = useState<boolean>(false)

  const [
    currentSortColumn,
    setCurrentSortColumn
  ] = useState<SortColumn>('chartDate')

  const [
    currentSortDirection,
    setCurrentSortDirection
  ] = useState<'asc' | 'desc'>('desc')

  type FetchCharts = {
    page: number
    sort?: string
    direction: 'asc' | 'desc'
  }

  const fetchCharts = async ({
    page = 1,
    sort,
    direction = 'asc'
  }: FetchCharts) => {
    if (!encounter.tenantId || encounter.patient && !encounter.patient.id) {
      console.error('Missing tenantId or patientId')
      return
    }

    setIsLoadingCharts(true)
    const limit = 15
    const offset = (page - 1) * limit

    const requestBody = {
      tenantId: encounter.tenantId,
      patientId: encounter.patient && encounter.patient.id,
      pagination: {
        limit,
        offset
      },
      sort: {
        column: sort || currentSortColumn || 'chartDate',
        direction
      }
    }

    try {
      const response = await api.post(API_URL.GET_CHARTS, requestBody)
      setChartsData(response.data as ChartsPayload)
    } catch (error) {
      console.error('Error fetching charts:', error)
    } finally {
      setIsLoadingCharts(false)
    }
  }

  const openFlyout = () => {
    setIsFlyoutOpen(true)
    if (!chartsData) {
      fetchCharts({
        page: 1,
        sort: currentSortColumn,
        direction: currentSortDirection
      })
    }
  }

  const closeFlyout = () => {
    setIsFlyoutOpen(false)
    setChartsData(null)
  }

  const handleColumnSort = (column: SortColumn) => {
    const isSameColumn = currentSortColumn === column

    let newDirection: 'asc' | 'desc' = 
      isSameColumn ? currentSortDirection === 'asc' ? 'desc'
        : 'asc'
      : SORT_COLUMNS[column].direction

    setCurrentSortColumn(column)
    setCurrentSortDirection(newDirection)

    const currentPage = chartsData && chartsData.pagination
      ? Math.floor(chartsData.pagination.offset / chartsData.pagination.limit) + 1
      : 1
    fetchCharts({
      page: currentPage,
      sort: column,
      direction: newDirection
    })
  }

  const renderSortIcon = (column: SortColumn) => {
    if (currentSortColumn !== column) return null

    return (
      <Icon
        name={
          currentSortDirection === 'asc'
            ? 'arrow up'
            : 'arrow down'
        }
        style={
          {
            marginLeft: '4px'
          }
        }
      />
    )
  }

  const handlePageChange = (page: number) => {
    fetchCharts({
      page,
      sort: currentSortColumn,
      direction: currentSortDirection
    })
  }

  const charts = chartsData && chartsData.charts || []
  const pagination = chartsData && chartsData.pagination
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.limit)
    : 0
  const currentPage = pagination
    ? Math.floor(pagination.offset / pagination.limit) + 1
    : 1
  const isSortingEnabled = charts.length > 0 && !isLoadingCharts

  const showEvidenceViewer = (chart: Chart) => (
    chart.sourceType === 'pdf' ||
    chart.sourceType === 'xml'
  )

  const [
    isDocumentModalOpen,
    setIsDocumentModalOpen
  ] = useState<boolean>(false)

  const [
    isLoadingDocument,
    setIsLoadingDocument
  ] = useState<boolean>(false)

  const [
    documentModalContentUrl,
    setDocumentModalContentUrl
  ] = useState<string>('')

  const [
    currentDocumentId,
    setCurrentDocumentId
  ] = useState<string>('')

  const [
    currentDocumentSourceType,
    setCurrentDocumentSourceType
  ] = useState<string>('')

  const fetchDocumentAndOpenModal = async (documentIdType: string, documentId: string) => {
    try {
      setIsLoadingDocument(true)
      setCurrentDocumentId(documentId)
      setCurrentDocumentSourceType(documentIdType)

      setChartsData(prevData => {
        if (!prevData) return prevData
        return {
          ...prevData,
          charts: prevData.charts.map(chart => {
            if (chart.documentId === documentId) {
              return {
                ...chart,
                lastReviewed: new Date().toDateString()
              }
            }
            return chart
          })
        }
      })

      if (encounter.tenantId && encounter.patient && encounter.patient.id) {
        api.post(API_URL.CHART_PATIENT_REVIEWED, {
          tenantId: encounter.tenantId,
          patientId: encounter.patient.id,
          documentId
        }).catch(error => {
          console.error('Error marking chart as reviewed:', error)
        })
      }

      const q = `?id=${documentId}&idType=${documentIdType}`
      const response = await api.get(API_URL.MDP_MODAL_URL + q)
      setDocumentModalContentUrl(response.data as string)
      setIsLoadingDocument(false)
      setIsDocumentModalOpen(true)
    } catch (error) {
      console.error('Error fetching document URL:', error)
      setIsLoadingDocument(false)
    }
  }

  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false)
    setDocumentModalContentUrl('')
    setCurrentDocumentId('')
    setCurrentDocumentSourceType('')
    setIsLoadingDocument(false)
  }

  const statusLabelBaseClassName = 'reveleer-status-label'

  const statusLabelClassName = (isReviewed: boolean) => isReviewed
    ? `${statusLabelBaseClassName} ${reveleerStatusLabelType.success}`
    : `${statusLabelBaseClassName} ${reveleerStatusLabelType.blocked}`

  return (
    <>
      <span
        id="cdi-chart-trigger"
        data-testid="cdi-chart-trigger"
        className="cursor-pointer"
        onClick={ openFlyout }
      >
        <Popup
          content={ 'Click to view the CDI Chart' }
          mouseEnterDelay={ 500 }
          trigger={
            shouldRenderReskin2025
              ? (
                <span className="pointer">
                   <ListIcon />
                </span>
              )
              : (
                <Icon
                  name="list"
                  size="large"
                  color="grey"
                  className="pointer"
                />
              )
          }
        />
      </span>
        { isLoadingDocument ?
          <Dimmer active inverted>
            <Loader inverted>Loading...</Loader>
          </Dimmer> :
          <>
          <Flyout
            data-testid="cdi-chart-flyout"
            isOpen={ isFlyoutOpen }
          >
            <>
              <div
                id="cdi-chart-flyout-header"
                data-testid="cdi-chart-flyout-header"
                className="flyout-header"
              >
                <h3
                  id="cdi-chart-flyout-header-title"
                  data-testid="cdi-chart-flyout-header-title"
                  className="reveleer"
                >
                  Chart Repository
                </h3>
                <div
                  id="cdi-chart-flyout-close-icon"
                  data-testid="cdi-chart-flyout-close-icon"
                  className="flyout-close-icon"
                  onClick={ closeFlyout }
                >
                  <Close />
                </div>
              </div>
              <div
                id="cdi-chart-flyout-content"
                data-testid="cdi-chart-flyout-content"
                className="flyout-content"
              >
                <div className="table-content">
                  <SafeTable
                    id="cdi-chart-table"
                    data-testid="cdi-chart-table"
                    className="ui none selectable compact table reskin"
                  >
                    <Table.Header>
                      <Table.Row
                        id="cdi-chart-table-header-row"
                        data-testid="cdi-chart-table-header-row"
                      >
                        <Table.HeaderCell
                          id="cdi-chart-table-header-chart-date"
                          data-testid="cdi-chart-table-header-chart-date"
                          onClick={ isSortingEnabled ? () => handleColumnSort('chartDate') : undefined }
                          style={ isSortingEnabled ? { cursor: 'pointer' } : {} }
                        >
                          {SORT_COLUMNS.chartDate.label}
                          { renderSortIcon('chartDate') }
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          id="cdi-chart-table-header-document-type"
                          data-testid="cdi-chart-table-header-document-type"
                          onClick={ isSortingEnabled ? () => handleColumnSort('documentType') : undefined }
                          style={ isSortingEnabled ? { cursor: 'pointer' } : {} }
                        >
                          {SORT_COLUMNS.documentType.label}
                          { renderSortIcon('documentType') }
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          id="cdi-chart-table-header-organization"
                          data-testid="cdi-chart-table-header-organization"
                          onClick={ isSortingEnabled ? () => handleColumnSort('organization') : undefined }
                          style={ isSortingEnabled ? { cursor: 'pointer' } : {} }
                        >
                          {SORT_COLUMNS.organization.label}
                          { renderSortIcon('organization') }
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          id="cdi-chart-table-header-chart-name"
                          data-testid="cdi-chart-table-header-chart-name"
                        >
                          Chart Name
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          id="cdi-chart-table-header-charts"
                          data-testid="cdi-chart-table-header-charts"
                        >
                          <span className="center">
                            Charts
                          </span>
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          id="cdi-chart-table-header-status"
                          data-testid="cdi-chart-table-header-status"
                        >
                          <span className="center">
                            Status
                          </span>
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      { isLoadingCharts ? (
                        <Table.Row
                          id="cdi-chart-table-row-loading"
                          data-testid="cdi-chart-table-row-loading"
                        >
                          <Table.Cell
                            id="cdi-chart-table-cell-loading"
                            data-testid="cdi-chart-table-cell-loading"
                            colSpan={ 6 }
                            style={ { textAlign: 'center', padding: '2rem' } }
                          >
                            <Loader active inline="centered" size="small" />
                          </Table.Cell>
                        </Table.Row>
                      ) : charts.length === 0 ? (
                        <Table.Row
                          id="cdi-chart-table-row-empty"
                          data-testid="cdi-chart-table-row-empty"
                        >
                          <Table.Cell
                            id="cdi-chart-table-cell-empty"
                            data-testid="cdi-chart-table-cell-empty"
                            colSpan={ 6 }
                            style={ { textAlign: 'center' } }
                          >
                            No charts found
                          </Table.Cell>
                        </Table.Row>
                      ) : (
                        charts.map((chart, index) => (
                          <Table.Row
                            key={ chart.documentId }
                            id={ `cdi-chart-table-row-${index}` }
                            data-testid={ `cdi-chart-table-row-${index}` }
                          >
                            <Table.Cell
                              id={ `cdi-chart-table-cell-chart-date-${index}` }
                              data-testid={ `cdi-chart-table-cell-chart-date-${index}` }
                            >
                              { chart.chartDate }
                            </Table.Cell>
                            <Table.Cell
                              id={ `cdi-chart-table-cell-document-type-${index}` }
                              data-testid={ `cdi-chart-table-cell-document-type-${index}` }
                            >
                              { chart.documentType }
                            </Table.Cell>
                            <Table.Cell
                              id={ `cdi-chart-table-cell-organization-${index}` }
                              data-testid={ `cdi-chart-table-cell-organization-${index}` }
                            >
                              { chart.organizationName }
                            </Table.Cell>
                            <Table.Cell
                              id={ `cdi-chart-table-cell-chart-name-${index}` }
                              data-testid={ `cdi-chart-table-cell-chart-name-${index}` }
                            >
                              { chart.documentName }
                            </Table.Cell>
                            <Table.Cell
                              id={ `cdi-chart-table-cell-charts-${index}` }
                              data-testid={ `cdi-chart-table-cell-charts-${index}` }
                            >
                              { showEvidenceViewer(chart) && (
                                <span>
                                  <Popup
                                    content="Evidence Viewer"
                                    trigger={
                                      <span
                                        id={ `cdi-chart-table-cell-charts-viewer-${index}` }
                                        data-testid={ `cdi-chart-table-cell-charts-viewer-${index}` }
                                        onClick={
                                          () => {
                                            fetchDocumentAndOpenModal(
                                              chart.documentIdType,
                                              chart.documentId
                                            )
                                          }
                                        }
                                        style={
                                          {
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }
                                        }
                                      >
                                        <DocumentIcon />
                                        <span>{ chart.sourceType.toUpperCase() }</span>
                                      </span>
                                    }
                                  />
                                </span>
                              ) }
                            </Table.Cell>
                            <Table.Cell
                              id={ `cdi-chart-table-cell-status-${index}` }
                              data-testid={ `cdi-chart-table-cell-status-${index}` }
                            >
                              <span className={ statusLabelClassName(chart.lastReviewed !== null) }>
                                { chart.lastReviewed ? 
                                  <Popup 
                                    content={ new Date(Date.parse(chart.lastReviewed)).toDateString() }
                                    trigger={
                                      <span
                                        style={
                                          {
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }
                                        }
                                      >
                                        Reviewed
                                      </span>
                                    }
                                        /> 
                                : <span>Unreviewed</span> 
                                }
                              </span>
                            </Table.Cell>
                          </Table.Row>
                        ))
                      ) }
                    </Table.Body>
                    <If isTrue={ totalPages >= 1 && !!pagination }>
                      <TablePaginationFooter
                        columns={ 6 }
                        currentPage={ currentPage }
                        totalPages={ totalPages }
                        onPageChange={ handlePageChange }
                      />
                    </If>
                  </SafeTable>
                </div >
              </div >
            </>
          </Flyout >
          <Modal
            id="cdi-chart-modal"
            data-testid="cdi-chart-modal"
            closeIcon
            open={ isDocumentModalOpen }
            onClose={ handleCloseDocumentModal }
          >
            { documentModalContentUrl ? (
              <iframe
                id="cdi-chart-modal-iframe"
                data-testid="cdi-chart-modal-iframe"
                title={ `Document Viewer - ${currentDocumentSourceType.toUpperCase()} - ${currentDocumentId}` }
                src={ documentModalContentUrl }
                style={ { width: '100%', height: '600px', border: 'none' } }
              />
            ) : (
              <p>Loading content...</p>
            ) }
          </Modal>
          </>
        }
    </>
  )
}

export default CDIChart

